import axios, { AxiosInstance } from 'axios'
import { showSampleReadonlyToast, showCurrentlyDisabledToast, showLlmKeyRequiredToast } from '../utils/toastApi'
import { store } from '../state/store'
import { setOnboardingVisible } from '../state/slices/onboardingSlice'
import { NEW_COMPANY_CTA_ENABLED } from '../config/featureGates'
import type { NarrativeStatusByField } from '../types/narrativeRefresh'
import type {
  ApiResponse,
  QuickModeResponse,
  DeepModePlanResponse,
  DeepModeExecutionResponse,
  ComprehensiveWorkflow,
  GraphData,
  PlanningPath,
  StrategyPlanningResponse,
  ProgressUpdate,
  EditRecord,
  ImpactAnalysisResponse,
  StrategySession,
} from '@models/index'
import type { DocType, DocumentResponse } from '@state/slices/documentSlice'

// Default API base URL - can be overridden by environment variable
// CRITICAL: Backend runs on port 8000, not 8001
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'
const LOGIN_URL = import.meta.env.VITE_LOGIN_URL || 'http://127.0.0.1:5000'

// Phase 6: async job handshake shape returned by POST /strategy/start (Express only)
export interface StrategyJobHandshake {
  status: 'pending'
  job_id: string
  conversation_id: number
  planning_path: 'express'
}

// Phase 6: errors thrown by pollStrategyJob
export class JobFailedError extends Error {
  constructor(public readonly errorCode: string) {
    super(`Strategy Planning job failed: ${errorCode}`)
    this.name = 'JobFailedError'
  }
}

export class PollingNetworkError extends Error {
  constructor(public readonly underlying: unknown) {
    super('Polling failed: 3 consecutive network errors')
    this.name = 'PollingNetworkError'
  }
}

export function _handleResponseError(error: any): Promise<never> {
  if (error?.response?.status === 401) {
    // Token expired or invalid
    localStorage.removeItem('jwt_token')
    window.location.href = LOGIN_URL
    return Promise.reject(error)
  }
  if (
    error?.response?.status === 403 &&
    error?.response?.data?.detail?.error === 'sample_readonly'
  ) {
    showSampleReadonlyToast(() => {
      if (NEW_COMPANY_CTA_ENABLED) {
        store.dispatch(setOnboardingVisible(true))
      } else {
        showCurrentlyDisabledToast()
      }
    })
    return Promise.reject(error)
  }
  if (
    error?.response?.status === 403 &&
    error?.response?.data?.detail?.error === 'llm_key_required'
  ) {
    showLlmKeyRequiredToast(() => {
      window.open('/settings?tab=model', '_blank')
    })
    return Promise.reject(error)
  }
  return Promise.reject(error)
}

class OmapApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 180000, // 3 minutes default (Quick mode, plan generation)
    })

    // Add request interceptor to attach JWT token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('jwt_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Add response interceptor for error handling
    this.client.interceptors.response.use((r) => r, _handleResponseError)
  }

  // Quick Mode - Fast tactical answers
  async submitQuickQuery(
    query: string,
    conversationId?: string,
    _history?: string,  // Deprecated - conversation history now loaded from backend via conversation_id
    companyId: number = 1
  ): Promise<QuickModeResponse> {
    // Convert conversation ID to integer (backend expects int, not string)
    const convIdNum = conversationId
      ? parseInt(conversationId.replace('conv_', ''))
      : undefined

    const response = await this.client.post<QuickModeResponse>('/query', {
      query: query,                  // Backend expects 'query', not 'user_input'
      company_id: companyId,         // Required by backend QueryRequest schema
      conversation_id: convIdNum,    // Backend expects integer or null
    })
    return response.data
  }

  // Deep Mode - Strategic reports
  async createPlan(
    query: string,
    conversationId?: string,
    _history?: string,  // Deprecated - conversation history now loaded from backend via conversation_id
    companyId: number = 1
  ): Promise<DeepModePlanResponse> {
    // Convert conversation ID to integer (backend expects int, not string)
    const convIdNum = conversationId
      ? parseInt(conversationId.replace('conv_', ''))
      : undefined

    const response = await this.client.post<DeepModePlanResponse>('/plan', {
      user_query: query,             // Backend expects 'user_query', not 'user_input'
      company_id: companyId,         // Required by backend PlanRequest schema
      user_id: 1,                    // Required by backend PlanRequest schema
      conversation_id: convIdNum,    // Backend expects integer or null
    })
    return response.data
  }

  async approvePlan(planId: string): Promise<DeepModeExecutionResponse> {
    const response = await this.client.post<DeepModeExecutionResponse>(
      `/plan/${planId}/approve`,
      {
        user_id: 1,                    // Required by backend PlanApprovalRequest schema
        excluded_hypotheses: [],       // Auto-approve all hypotheses
        validated_nodes: [],           // No explicit validations (could enhance later)
      },
      {
        timeout: 360000,               // 6 minutes for Deep mode execution (tournament + report generation)
      }
    )
    return response.data
  }

  // Comprehensive Mode - Quality-driven 3-gate workflow
  async startComprehensive(query: string, conversationId?: string, companyId: number = 1): Promise<ComprehensiveWorkflow> {
    // Convert conversation ID to integer (backend expects int)
    const convIdNum = conversationId ? parseInt(conversationId.replace('conv_', '')) : Date.now()

    const response = await this.client.post<ComprehensiveWorkflow>('/comprehensive/start', {
      user_query: query,           // Backend expects 'user_query', not 'user_input'
      company_id: companyId,       // Company ID from Redux state
      user_id: 1,                  // Default user ID
      conversation_id: convIdNum,  // Backend expects integer, not string
    })
    return response.data
  }

  async advanceComprehensive(workflowId: string, payload: any): Promise<ComprehensiveWorkflow> {
    // Extract conversation_id from payload and convert to int
    const convIdNum = payload.conversation_id
      ? (typeof payload.conversation_id === 'string'
          ? parseInt(payload.conversation_id.replace('conv_', ''))
          : payload.conversation_id)
      : parseInt(workflowId)

    const response = await this.client.post<ComprehensiveWorkflow>(
      '/comprehensive/advance',
      {
        conversation_id: convIdNum,  // Backend expects integer
        user_id: 1,                  // Default user ID
        decision_payload: payload,   // Backend expects this wrapper
      },
      {
        timeout: 360000,             // 6 minutes for Comprehensive mode gates (MECE generation, Strategy Map, etc.)
      }
    )
    return response.data
  }

  async updateWorkspace(
    conversationId: string,
    updates: { mece_table?: any }
  ): Promise<any> {
    // Convert conversation ID to integer (backend expects int, not string)
    const conversationIdInt = parseInt(conversationId.replace('conv_', ''))

    const response = await this.client.patch('/comprehensive/workspace', {
      conversation_id: conversationIdInt,
      user_id: 1,
      updates
    })

    return response.data
  }

  // ==========================================================================
  // Strategy Planning Mode (Jan 2026) - Unified Quick + Strategy Planning
  // ==========================================================================

  /**
   * Start Strategy Planning workflow
   * @param query - User's strategic question
   * @param conversationId - Conversation ID (string or number)
   * @param planningPath - 'express' | 'guided' | 'explore'
   */
  async startStrategy(
    query: string,
    conversationId: string | number,
    planningPath: PlanningPath = 'guided',
    companyId: number = 1
  ): Promise<StrategyPlanningResponse | StrategyJobHandshake> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.post<StrategyPlanningResponse | StrategyJobHandshake>(
      '/strategy/start',
      {
        user_query: query,
        company_id: companyId,
        user_id: 1,
        conversation_id: convIdNum,
        planning_path: planningPath,
      },
      {
        // 60s ceiling now — the Express handshake returns in <100ms; Guided + Explore
        // are well under a minute. The 10-minute timeout from pre-Phase-6 is dead.
        timeout: 60000,
      }
    )
    return response.data
  }

  /**
   * Advance Strategy Planning workflow through gates
   * @param conversationId - Conversation ID
   * @param payload - Decision payload (approved, feedback, modifications, selected_pillars)
   */
  async advanceStrategy(
    conversationId: string | number,
    payload: {
      approved: boolean
      feedback?: string
      selected_option?: string
      modifications?: any
      selected_pillars?: string[]  // Phase 1.1: Selected pillars for Gate 3
    }
  ): Promise<StrategyPlanningResponse> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.post<StrategyPlanningResponse>(
      '/strategy/advance',
      {
        conversation_id: convIdNum,
        user_id: 1,
        decision_payload: payload,
      },
      {
        timeout: 360000,  // 6 minutes for gate processing
      }
    )
    return response.data
  }

  /**
   * Get real-time progress for Strategy Planning workflow
   * @param conversationId - Conversation ID
   */
  async getStrategyProgress(conversationId: string | number): Promise<{
    status: string
    progress: ProgressUpdate
    timestamp: string
  }> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.get(`/strategy/${convIdNum}/progress`)
    return response.data
  }

  /**
   * Phase 6: poll /strategy/{conv_id}/progress until async_job reaches a
   * terminal state. Resolves with the full Express result payload on
   * status='complete'; rejects with JobFailedError on status='failed';
   * rejects with PollingNetworkError after 3 consecutive network errors.
   *
   * @param conversationId Conversation ID (string or number)
   * @param opts.intervalMs Polling interval in milliseconds (default 5000)
   */
  async pollStrategyJob(
    conversationId: string | number,
    opts: { intervalMs?: number } = {}
  ): Promise<StrategyPlanningResponse> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId
    const interval = opts.intervalMs ?? 5000
    const MAX_CONSECUTIVE_ERRORS = 3
    let consecutiveErrors = 0

    return new Promise<StrategyPlanningResponse>((resolve, reject) => {
      const tick = async () => {
        try {
          const response = await this.client.get(`/strategy/${convIdNum}/progress`)
          const data = response.data
          consecutiveErrors = 0

          const status = data?.async_job?.status
          if (status === 'complete' && data.result) {
            resolve(data.result as StrategyPlanningResponse)
            return
          }
          if (status === 'failed') {
            reject(new JobFailedError(data.async_job?.error || 'unknown_failure'))
            return
          }
          // pending or running → schedule next tick
          setTimeout(tick, interval)
        } catch (err) {
          consecutiveErrors += 1
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            reject(new PollingNetworkError(err))
            return
          }
          setTimeout(tick, interval)
        }
      }

      setTimeout(tick, interval)
    })
  }

  /**
   * Sync workspace edits to Analysis Report
   * @param conversationId - Conversation ID
   * @param pendingEdits - Array of edit records
   * @param currentDocument - Current Analysis Report markdown
   * @param workspaceState - Current workspace state
   */
  async syncStrategyReport(
    conversationId: string | number,
    currentDocument: string,
    workspaceState: any,
    editSource?: 'mece' | 'strategy_map',
    pendingEdits: EditRecord[] = []
  ): Promise<{ document: string; synced_sections: string[] }> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.post('/strategy/sync-report', {
      conversation_id: convIdNum,
      pending_edits: pendingEdits,
      current_document: currentDocument,
      workspace_state: workspaceState,
      edit_source: editSource,
    })
    return response.data
  }

  /**
   * Analyze the impact of workspace edits before syncing
   * @param conversationId - Conversation ID
   * @param editDescription - Description of the edit
   * @param oldValue - Value before edit
   * @param newValue - Value after edit
   * @param pillarName - Optional pillar name for context
   * @param meceSummary - Optional MECE summary for context
   */
  async analyzeStrategyImpact(
    conversationId: string | number,
    editDescription: string,
    oldValue: any,
    newValue: any,
    pillarName?: string,
    meceSummary?: any
  ): Promise<ImpactAnalysisResponse> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.post('/strategy/analyze-impact', {
      conversation_id: convIdNum,
      edit_description: editDescription,
      old_value: oldValue,
      new_value: newValue,
      pillar_name: pillarName,
      mece_summary: meceSummary,
    })
    return response.data
  }

  /**
   * List resumable Strategy Planning sessions
   * @param companyId - Company ID (defaults to 1)
   */
  async listStrategySessions(companyId: number = 1): Promise<StrategySession[]> {
    const response = await this.client.get('/strategy/sessions', {
      params: { company_id: companyId }
    })
    return response.data
  }

  /**
   * Resume a paused Strategy Planning session
   * @param sessionId - Session ID to resume
   */
  async resumeStrategySession(sessionId: string): Promise<StrategyPlanningResponse> {
    const response = await this.client.post(`/strategy/sessions/${sessionId}/resume`)
    return response.data
  }

  // Graph Visualization - Fetch ontology graph data
  async fetchOntologyGraph(_filters?: { taxonomyLayers?: string[] }): Promise<GraphData> {
    // For now, load from local JSON file since backend doesn't have this endpoint yet
    const response = await axios.get('/graph.json')

    // Transform the data to match our GraphData type
    const rawData = response.data
    const nodes = rawData.nodes.map((node: any) => ({
      id: node.data.id,
      label: node.data.label,
      label_slug: node.data.label_slug,
      meta_id: node.data.meta_id,
      meta_name: node.data.meta_name,
      meta_description: node.data.meta_description,
      shape_type: node.data.shape_type,
      width: node.data.width,
      height: node.data.height,
      font_size: node.data.font_size,
      x: node.position.x,
      y: node.position.y,
    }))

    const edges = rawData.edges?.map((edge: any) => ({
      id: edge.data.id,
      source: edge.data.source,
      target: edge.data.target,
      label: edge.data.label,
      relationship_type: edge.data.relationship_type || 'UNKNOWN',
    })) || []

    return { nodes, edges }
  }

  // Fetch node paths from Neo4j
  async fetchNodePaths(nodeId: string): Promise<any> {
    const response = await this.client.get(`/graph/paths/${nodeId}`)
    return response.data
  }

  // Strategy Maps
  async generateStrategyMap(meceTable: any): Promise<{ strategy_map: string }> {
    const response = await this.client.post('/strategy-map/generate', {
      mece_table: meceTable,
    })
    return response.data
  }

  async saveStrategyMap(mapData: any): Promise<ApiResponse<any>> {
    const response = await this.client.post('/strategy-map/save', mapData)
    return response.data
  }

  /**
   * Phase 2.3: Regenerate Strategy Map from current MECE table
   * Called when Strategy Map is stale after MECE edits
   * @param conversationId - Conversation ID
   */
  async regenerateStrategyMap(conversationId: string | number): Promise<{
    strategy_map: any
    success: boolean
  }> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.post('/strategy/regenerate-map', {
      conversation_id: convIdNum,
      user_id: 1,
    }, {
      timeout: 120000,  // 2 minutes for strategy map regeneration
    })
    return response.data
  }

  // Initiative Management (to be implemented on backend)
  async createInitiative(data: any): Promise<ApiResponse<any>> {
    const response = await this.client.post('/initiatives', data)
    return response.data
  }

  async updateInitiative(id: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.client.put(`/initiatives/${id}`, data)
    return response.data
  }

  async fetchInitiatives(filters?: any): Promise<ApiResponse<any>> {
    const response = await this.client.get('/initiatives', { params: filters })
    return response.data
  }

  async deleteInitiative(id: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/initiatives/${id}`)
    return response.data
  }

  // Conversation Persistence - Retrieve saved conversations
  async fetchConversations(companyId: number = 1): Promise<any> {
    const params = { company_id: companyId }
    const response = await this.client.get('/conversations', { params })
    return response.data
  }

  async fetchConversation(conversationId: string): Promise<any> {
    // Convert frontend string format (conv_123) to backend int format (123)
    const convIdNum = parseInt(conversationId.replace('conv_', ''))
    const response = await this.client.get(`/conversations/${convIdNum}`)
    return response.data
  }

  async deleteConversation(conversationId: string): Promise<any> {
    // Convert frontend string format (conv_123) to backend int format (123)
    const convIdNum = parseInt(conversationId.replace('conv_', ''))
    const response = await this.client.delete(`/conversations/${convIdNum}`)
    return response.data
  }

  async renameConversation(conversationId: string, newTitle: string): Promise<any> {
    // Convert frontend string format (conv_123) to backend int format (123)
    const convIdNum = parseInt(conversationId.replace('conv_', ''))

    // Validate title client-side (fail fast)
    const trimmedTitle = newTitle.trim()
    if (!trimmedTitle) {
      throw new Error('Title cannot be empty')
    }
    if (trimmedTitle.length > 200) {
      throw new Error('Title must be 200 characters or less')
    }

    const response = await this.client.patch(`/conversations/${convIdNum}`, {
      title: trimmedTitle
    })
    return response.data
  }

  // ==========================================================================
  // Phase 3.2: Annotation System
  // ==========================================================================

  /**
   * Add annotation to a tactic
   * @param conversationId - Conversation ID
   * @param tacticId - Tactic initiative ID
   * @param content - Annotation text
   * @param annotationType - Type of annotation
   * @param author - Author name (optional)
   */
  async addAnnotation(
    conversationId: string | number,
    tacticId: string,
    content: string,
    annotationType: 'note' | 'concern' | 'question' | 'action_item' = 'note',
    author: string = 'User'
  ): Promise<{ success: boolean; annotation: any }> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.post(
      `/comprehensive/workspace/${convIdNum}/annotations`,
      {
        tactic_id: tacticId,
        content,
        annotation_type: annotationType,
        author
      }
    )
    return response.data
  }

  /**
   * Delete an annotation
   * @param conversationId - Conversation ID
   * @param annotationId - Annotation ID to delete
   */
  async deleteAnnotation(
    conversationId: string | number,
    annotationId: string
  ): Promise<{ success: boolean }> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.delete(
      `/comprehensive/workspace/${convIdNum}/annotations/${annotationId}`
    )
    return response.data
  }

  // ==========================================================================
  // Phase 3.3: Implementation Status Tracking
  // ==========================================================================

  /**
   * Update implementation status for a tactic
   * @param conversationId - Conversation ID
   * @param tacticId - Tactic initiative ID
   * @param status - New implementation status
   */
  async updateTacticStatus(
    conversationId: string | number,
    tacticId: string,
    status: 'not_started' | 'planning' | 'in_progress' | 'completed' | 'blocked' | 'not_applicable'
  ): Promise<{ success: boolean; status: string }> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.patch(
      `/comprehensive/workspace/${convIdNum}/tactics/${tacticId}/status`,
      { status }
    )
    return response.data
  }

  /**
   * Phase 3.4: Export strategy playbook
   *
   * Generates a customized markdown document containing:
   * - Executive summary
   * - Strategic pillars with tactics
   * - Strategy map overview
   * - Implementation roadmap
   */
  async exportPlaybook(
    conversationId: string | number,
    config: {
      include_executive_summary?: boolean
      include_mece_pillars?: boolean
      include_evidence?: boolean
      include_annotations?: boolean
      include_strategy_map?: boolean
      include_roadmap?: boolean
      filter_mode?: 'all' | 'in_progress' | 'prioritized' | 'completed' | 'not_started'
      format?: 'markdown' | 'html'
    } = {}
  ): Promise<{ success: boolean; content: string; format: string }> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.post('/strategy/export-playbook', {
      conversation_id: convIdNum,
      config
    })
    return response.data
  }

  /**
   * Phase 5.2: Apply a scenario's selections as the active configuration.
   *
   * Applies the tactic selections from a scenario to the workflow state,
   * marking the strategy map as needing regeneration.
   */
  async applyScenario(
    conversationId: string | number,
    scenarioId: string,
    scenarioData: {
      name: string
      tactic_selections: Array<{
        pillar_index: number
        tactic_index: number
        inclusion: 'included' | 'excluded' | 'maybe'
      }>
    }
  ): Promise<{ success: boolean; selected_pillars: string[]; strategy_map_stale: boolean }> {
    const convIdNum = typeof conversationId === 'string'
      ? parseInt(conversationId.replace('conv_', ''))
      : conversationId

    const response = await this.client.post(
      `/comprehensive/workspace/${convIdNum}/scenarios/${scenarioId}/apply`,
      scenarioData
    )
    return response.data
  }

  // --- Agency API ---

  async getAgencyPending(companyId: number) {
    const resp = await this.client.get(`/agency/pending/${companyId}`);
    return resp.data;
  }

  async getAgencyAction(actionId: number) {
    const resp = await this.client.get(`/agency/actions/${actionId}`);
    return resp.data;
  }

  async approveAction(actionId: number) {
    const resp = await this.client.post(`/agency/actions/${actionId}/approve`);
    return resp.data;
  }

  async rejectAction(actionId: number) {
    const resp = await this.client.post(`/agency/actions/${actionId}/reject`);
    return resp.data;
  }

  async rollbackAction(actionId: number) {
    const resp = await this.client.post(`/agency/actions/${actionId}/rollback`);
    return resp.data;
  }

  async getAgencyAudit(companyId: number, limit: number = 50) {
    const resp = await this.client.get(`/agency/audit/${companyId}?limit=${limit}`);
    return resp.data;
  }

  async getAutonomyProfile(companyId: number) {
    const resp = await this.client.get(`/agency/profile/${companyId}`);
    return resp.data;
  }

  async updateAutonomyProfile(companyId: number, profile: any) {
    const resp = await this.client.put(`/agency/profile/${companyId}`, profile);
    return resp.data;
  }

  async getAgencyRecent(companyId: number, limit: number = 20) {
    const resp = await this.client.get(`/agency/recent/${companyId}?limit=${limit}`);
    return resp.data;
  }

  async getCircuitBreakerState(companyId: number) {
    const resp = await this.client.get(`/agency/circuit-breaker/${companyId}`);
    return resp.data;
  }

  // --- Vigil API ---

  async getVigilNotifications(companyId: number) {
    const resp = await this.client.get(`/vigil/notifications/${companyId}`);
    return resp.data;
  }

  async dismissVigilNotification(notificationId: number) {
    const resp = await this.client.post(`/vigil/notifications/${notificationId}/dismiss`);
    return resp.data;
  }

  // --- Documents API ---

  async getDocument(docType: DocType, nodeId: string): Promise<DocumentResponse> {
    const resp = await this.client.get<DocumentResponse>(`/documents/${docType}/${nodeId}`);
    return resp.data;
  }

  // --- Dossier API ---

  async getDossierIndex(companyId: number) {
    const resp = await this.client.get(`/dossier/${companyId}`);
    return resp.data;
  }

  async getDossierPillar(companyId: number, pillar: string) {
    const resp = await this.client.get(`/dossier/${companyId}/pillar/${pillar}`);
    return resp.data;
  }

  async triggerConsolidation(companyId: number) {
    const resp = await this.client.post(`/dossier/${companyId}/consolidate`);
    return resp.data;
  }

  async refreshOrientation(conversationId: number) {
    const resp = await this.client.post(`/conversations/${conversationId}/orientation`);
    return resp.data;
  }

  // --- Portfolio API ---

  async getPortfolioCompanies(userId: number): Promise<{ companies: any[] }> {
    const response = await this.client.get(`/portfolio/${userId}/companies`)
    return response.data
  }

  // --- Feature Flags API ---

  async getCompanyFlags(companyId: number) {
    const resp = await this.client.get(`/feature-flags/${companyId}`);
    return resp.data;
  }

  async setCompanyFlag(companyId: number, flagName: string, enabled: boolean) {
    const resp = await this.client.put(`/feature-flags/${companyId}/${flagName}`, { enabled });
    return resp.data;
  }

  async deleteCompanyFlag(companyId: number, flagName: string) {
    const resp = await this.client.delete(`/feature-flags/${companyId}/${flagName}`);
    return resp.data;
  }

  async resetCircuitBreaker(companyId: number) {
    const resp = await this.client.post(`/agency/circuit-breaker/${companyId}/reset`);
    return resp.data;
  }

  // --- User LLM credential (BYO key) ---
  // Every endpoint derives user_id from the JWT; never send user_id explicitly.

  async getLlmProviders() {
    const resp = await this.client.get('/user/llm-providers');
    return resp.data.providers as Array<{
      slug: string; label: string; base_url: string; supports_json_mode: boolean;
    }>;
  }

  async getLlmCredential() {
    const resp = await this.client.get('/user/llm-credential');
    return resp.data.credential as null | {
      provider: string; base_url: string; model: string; key_last4: string;
      supports_json_mode: boolean; status: string;
      last_tested_at: string | null; last_error: string | null;
    };
  }

  async saveLlmCredential(payload: {
    provider: string; base_url: string; model: string;
    api_key?: string; supports_json_mode?: boolean;
  }) {
    const resp = await this.client.put('/user/llm-credential', payload);
    return resp.data.credential;
  }

  async testLlmCredential() {
    const resp = await this.client.post('/user/llm-credential/test');
    return resp.data as { status: 'ok' | 'failed'; error?: string };
  }

  async deleteLlmCredential() {
    const resp = await this.client.delete('/user/llm-credential');
    return resp.data;
  }

  async deleteCompany(companyId: number): Promise<{
    deleted: boolean
    company_id: number
    total_rows: number
    rows_deleted: Record<string, number>
    visuals: { removed: boolean; files: number; reason?: string }
  }> {
    const resp = await this.client.delete(`/companies/${companyId}`)
    return resp.data
  }

  // --- Company Profile API ---

  /**
   * Fetch all company profile node values for a company.
   * Returns the `values` map keyed by node_id (e.g. mvv_id, company_mission_id,
   * competitive_vision_id) so callers can index by node_id directly.
   */
  async getAllCompanyProfileValues(companyId: number): Promise<Record<string, string>> {
    const response = await this.client.get<{
      company_id: number
      values: Record<string, string>
    }>(`/company-profile/${companyId}`)
    return response.data.values ?? {}
  }

  /**
   * Phase 2-E: Regenerate the strategy plan / Analysis Report from the current
   * workspace state (including any persisted target edits). Returns the freshly
   * generated full report markdown plus a server-supplied generated_at timestamp.
   */
  async regenerateStrategyPlan(
    conversationId: string
  ): Promise<{
    ok: true
    fullReport: string
    generatedAt: string
    narrativeRefreshStatus?: 'refreshed' | 'partial' | 'skipped'
    narrativeStatusByField?: NarrativeStatusByField
  }> {
    // Backend conversation_id column is bigint — strip the "conv_" prefix and
    // coerce to int (matches every other method in this client).
    const convIdNum = parseInt(conversationId.replace('conv_', ''))
    const response = await this.client.post(
      '/strategy_plan/regenerate',
      { conversation_id: convIdNum }
    )
    return response.data
  }

  /**
   * Phase 5: POST /strategy_plan/refresh-narratives. Functionally identical
   * to regenerateStrategyPlan in terms of work performed; separate URL +
   * audit prefix + rate-limit bucket per spec D7.
   */
  async refreshNarratives(
    conversationId: string
  ): Promise<{
    ok: true
    fullReport: string
    generatedAt: string
    narrativeRefreshStatus?: 'refreshed' | 'partial' | 'skipped'
    narrativeStatusByField?: NarrativeStatusByField
    narrativesRefreshedAt: string
  }> {
    const convIdNum = parseInt(conversationId.replace('conv_', ''))
    const response = await this.client.post(
      '/strategy_plan/refresh-narratives',
      { conversation_id: convIdNum }
    )
    return response.data
  }
}

export const omapApi = new OmapApiClient()
